import React from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Link,
  LinearProgress,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import RefreshIcon from '@mui/icons-material/Refresh';
import BackofficeReportsApi from './backofficeReportsApi';

const BUSINESS_TIMEZONE = 'Europe/Bucharest';
const AUTO_REFRESH_MS = 60000;

const validDateTime = value => typeof value === 'string' && Number.isFinite(Date.parse(value));
const formatRemaining = milliseconds => {
  const seconds = Math.max(0, Math.ceil(milliseconds / 1000));
  return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;
};

const DELIVERY_STATUS_OPTIONS = [
  { value: '0', label: 'Not applicable' },
  { value: '1', label: 'Registered' },
  { value: '2', label: 'Assigned' },
  { value: '3', label: 'Picked up' },
  { value: '4', label: 'On the way' },
  { value: '5', label: 'Delivered' },
  { value: '6', label: 'Failed' },
  { value: '9', label: 'Undefined' }
];

export const getBusinessDate = (value = new Date()) => {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: BUSINESS_TIMEZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  }).formatToParts(value).reduce((result, part) => ({
    ...result,
    [part.type]: part.value
  }), {});
  return `${parts.year}-${parts.month}-${parts.day}`;
};

export const shiftIsoDate = (value, days) => {
  const [year, month, day] = String(value).split('-').map(Number);
  const shifted = new Date(Date.UTC(year, month - 1, day + days));
  return [
    shifted.getUTCFullYear(),
    String(shifted.getUTCMonth() + 1).padStart(2, '0'),
    String(shifted.getUTCDate()).padStart(2, '0')
  ].join('-');
};

const formatDateTime = value => {
  if (!value) return 'Not reported';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return 'Not reported';
  return new Intl.DateTimeFormat('ro-RO', {
    timeZone: BUSINESS_TIMEZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23'
  }).format(parsed);
};

const getPhoneLink = value => {
  const phone = String(value || '').trim();
  return /^\+[1-9][0-9]{6,14}$/.test(phone) ? `tel:${phone}` : '';
};

const getTrackingLink = value => {
  try {
    const parsed = new URL(String(value || ''));
    const hostname = parsed.hostname.toLowerCase();
    if (
      parsed.protocol !== 'https:' ||
      parsed.username ||
      parsed.password ||
      (parsed.port && parsed.port !== '443') ||
      (hostname !== 'glovoapp.com' && !hostname.endsWith('.glovoapp.com'))
    ) {
      return '';
    }
    return parsed.toString();
  } catch {
    return '';
  }
};

const getErrorMessage = error => (
  error?.responseJSON?.detail ||
  error?.responseJSON?.message ||
  error?.message ||
  'Could not load delivery orders.'
);

const getDeliveryStatusColor = status => {
  const value = Number(status);
  if (value === 5) return 'success';
  if (value === 6) return 'error';
  if (value === 3 || value === 4) return 'info';
  if (value === 1 || value === 2) return 'warning';
  return 'default';
};

const getShortOrderNumber = (code, iref) => {
  const trailingDigits = String(code || '').match(/(\d+)$/)?.[1];
  return trailingDigits ? trailingDigits.slice(-4) : String(iref ?? '—');
};

const InfoItem = ({ label, children }) => (
  <Box sx={{ minWidth: 0 }}>
    <Typography
      variant="caption"
      sx={{ display: 'block', color: '#64748b', fontWeight: 700 }}
    >
      {label}
    </Typography>
    <Typography
      component="div"
      variant="body2"
      sx={{ color: '#111827', overflowWrap: 'anywhere' }}
    >
      {children ?? '—'}
    </Typography>
  </Box>
);

const CourierDetails = ({ dispatch }) => {
  if (!dispatch) {
    return (
      <Alert severity="info" sx={{ mt: 1 }}>
        No managed courier record is stored for this order.
      </Alert>
    );
  }

  const phoneLink = getPhoneLink(dispatch.courierPhone);
  const trackingLink = getTrackingLink(dispatch.trackingLink);
  return (
    <Box
      sx={{
        mt: 1,
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
        gap: 1.25
      }}
    >
      <InfoItem label="Dispatch status">{dispatch.status || 'Not reported'}</InfoItem>
      <InfoItem label="Generation">{dispatch.generation ?? '—'}</InfoItem>
      <InfoItem label="Last update">{formatDateTime(dispatch.updatedOn)}</InfoItem>
      {dispatch.providerStatus && (
        <InfoItem label="Glovo status">{dispatch.providerStatus}</InfoItem>
      )}
      {dispatch.providerStatus === 'CANCELLED' && (
        <InfoItem label="Glovo cancellation reason">
          {dispatch.cancellationReasonLabel || dispatch.cancellationReason || 'Not reported by Glovo'}
        </InfoItem>
      )}
      {dispatch.cancellationSource && (
        <InfoItem label="Glovo cancellation source">{dispatch.cancellationSource}</InfoItem>
      )}
      {dispatch.providerOrderId && (
        <InfoItem label="Glovo order ID">{dispatch.providerOrderId}</InfoItem>
      )}
      {dispatch.courierName && (
        <InfoItem label="Courier name">{dispatch.courierName}</InfoItem>
      )}
      {dispatch.courierPhone && (
        <InfoItem label="Courier phone">
          {phoneLink ? <Link href={phoneLink}>{dispatch.courierPhone}</Link> : dispatch.courierPhone}
        </InfoItem>
      )}
      {dispatch.estimatedPickupOn && (
        <InfoItem label="Estimated pickup">{formatDateTime(dispatch.estimatedPickupOn)}</InfoItem>
      )}
      {dispatch.estimatedDeliveryOn && (
        <InfoItem label="Estimated delivery">{formatDateTime(dispatch.estimatedDeliveryOn)}</InfoItem>
      )}
      {dispatch.pickedUpOn && (
        <InfoItem label={dispatch.errorCode === 'CANCEL_AFTER_PICKUP_CONFLICT'
          ? 'Pickup evidence recorded' : 'Picked up'}>
          {formatDateTime(dispatch.pickedUpOn)}
        </InfoItem>
      )}
      {trackingLink && (
        <InfoItem label="Tracking">
          <Link href={trackingLink} target="_blank" rel="noopener noreferrer">
            Open Glovo tracking
          </Link>
        </InfoItem>
      )}
      {dispatch.errorCode && (
        <InfoItem label="Attention code">{dispatch.errorCode}</InfoItem>
      )}
    </Box>
  );
};

const DeliveryOrderRow = ({ order, onRefreshCourier, action, nowMs, reportBusy, courierBusy }) => {
  const total = Number(order.totalAmount || 0).toFixed(2);
  const dispatch = order.courierDispatch;
  const currentAction = action?.generation === dispatch?.generation ? action : null;
  const nextAllowedOn = currentAction?.nextAllowedOn || order.courierRefresh?.nextAllowedOn;
  const remaining = validDateTime(nextAllowedOn) ? Math.max(0, Date.parse(nextAllowedOn) - nowMs) : 0;
  const isFailed = Number(order.deliveryStatus) === 6;
  const shortOrderNumber = getShortOrderNumber(order.code, order.iref);
  const contactPhoneLink = getPhoneLink(order.contactPhone);
  const courierSummary = dispatch
    ? (dispatch.courierName || 'Courier not assigned')
    : 'No dispatch record';
  return (
    <Accordion
      disableGutters
      elevation={0}
      sx={{
        borderBottom: '1px solid rgba(15, 23, 42, 0.08)',
        borderLeft: isFailed ? '4px solid #d32f2f' : '4px solid transparent',
        bgcolor: isFailed ? 'rgba(211, 47, 47, 0.035)' : 'transparent',
        '&:before': { display: 'none' },
        '&.Mui-expanded': {
          m: 0,
          bgcolor: isFailed ? 'rgba(211, 47, 47, 0.055)' : 'rgba(26, 35, 126, 0.02)'
        }
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        aria-controls={`delivery-order-${order.iref}-content`}
        id={`delivery-order-${order.iref}-header`}
        sx={{
          minHeight: 52,
          '&.Mui-expanded': { minHeight: 52 },
          '& .MuiAccordionSummary-content': { my: 0.75 },
          '& .MuiAccordionSummary-content.Mui-expanded': { my: 0.75 }
        }}
      >
        <Box
          sx={{
            width: '100%',
            pr: 1,
            display: 'grid',
            gridTemplateColumns: {
              xs: 'minmax(0, 1fr)',
              sm: 'minmax(180px, 1.25fr) 165px minmax(220px, 1fr)'
            },
            gap: { xs: 0.75, sm: 1.5 },
            alignItems: 'center'
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ color: '#1a237e', fontWeight: 800 }}>
              Order {shortOrderNumber}
              <Typography component="span" variant="caption" sx={{ ml: 0.75, color: '#64748b', fontWeight: 700 }}>
                · #{order.iref}
              </Typography>
            </Typography>
            <Typography variant="body2" noWrap title={order.location?.name || ''}>
              {order.location?.name || 'Unknown location'}
            </Typography>
          </Box>
          <Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums' }}>
            {formatDateTime(order.scheduledOn)}
          </Typography>
          <Stack
            direction="row"
            spacing={0}
            alignItems="center"
            flexWrap="wrap"
            sx={{ minWidth: 0, gap: 0.75 }}
          >
            <Chip
              size="small"
              color={getDeliveryStatusColor(order.deliveryStatus)}
              variant={isFailed ? 'filled' : 'outlined'}
              label={isFailed ? 'Delivery failed' : (order.deliveryStatusLabel || 'Unknown')}
            />
            <Typography variant="caption" sx={{ color: '#64748b' }}>
              {courierSummary}
            </Typography>
          </Stack>
        </Box>
      </AccordionSummary>
      <AccordionDetails
        id={`delivery-order-${order.iref}-content`}
        sx={{ pt: 0, pb: 1.5 }}
      >
        {isFailed && (
          <Alert severity="error" sx={{ mb: 1.25 }}>
            Delivery failed. Review the stored courier dispatch details below.
          </Alert>
        )}
        <Typography variant="subtitle2" sx={{ color: '#1a237e', mb: 0.75 }}>
          Order information
        </Typography>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, minmax(0, 1fr))' },
            gap: 1.25
          }}
        >
          <InfoItem label="Order code">{order.code || '—'}</InfoItem>
          <InfoItem label="Created">{formatDateTime(order.createdOn)}</InfoItem>
          <InfoItem label="Scheduled">{formatDateTime(order.scheduledOn)}</InfoItem>
          <InfoItem label="Promised date">{order.promisedDate || '—'}</InfoItem>
          <InfoItem label="Order status">{order.orderStatusLabel || order.orderStatus}</InfoItem>
          <InfoItem label="Delivery progress">
            {order.deliveryStatusLabel || order.deliveryStatus}
          </InfoItem>
          <InfoItem label="Delivery contact">{order.contactName || '—'}</InfoItem>
          <InfoItem label="Delivery phone">
            {contactPhoneLink ? <Link href={contactPhoneLink}>{order.contactPhone}</Link> : (order.contactPhone || '—')}
          </InfoItem>
          <InfoItem label="Lines">{order.numberOfLines ?? 0}</InfoItem>
          <InfoItem label="Total">{total}</InfoItem>
        </Box>
        <Typography variant="subtitle2" sx={{ color: '#1a237e', mt: 1.5 }}>
          Courier dispatch
        </Typography>
        {dispatch?.providerStatus === 'CANCELLED' && (
          <Alert severity="warning" sx={{ mt: 1 }}>
            Glovo reports this courier attempt as cancelled. Any reason below is reported by Glovo.
          </Alert>
        )}
        {dispatch?.errorCode === 'CANCEL_AFTER_PICKUP_CONFLICT' && (
          <Alert severity="error" sx={{ mt: 1 }}>
            Glovo cancellation conflicts with pickup evidence recorded by Kalopsia.
            Confirm directly with the customer whether anything was received.
          </Alert>
        )}
        <CourierDetails dispatch={dispatch} />
        {order.courierRefresh?.eligible === true && (
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{ mt: 1.25, gap: 0.5 }}>
            <Button
              size="small"
              variant="outlined"
              startIcon={currentAction?.loading ? <CircularProgress size={14} /> : <RefreshIcon />}
              disabled={reportBusy || courierBusy || remaining > 0}
              onClick={() => onRefreshCourier(order)}
            >
              Refresh courier details
            </Button>
            {remaining > 0 && (
              <Typography variant="caption" sx={{ color: '#64748b', fontVariantNumeric: 'tabular-nums' }}>
                Available in {formatRemaining(remaining)}
              </Typography>
            )}
          </Stack>
        )}
        {currentAction?.error && <Alert severity="warning" sx={{ mt: 1 }}>{currentAction.error}</Alert>}
      </AccordionDetails>
    </Accordion>
  );
};

const DeliveryOrders = ({ client, initialDate, locations = [] }) => {
  const today = getBusinessDate();
  const [selectedDate, setSelectedDate] = React.useState(
    initialDate && initialDate <= today ? initialDate : today
  );
  const [selectedLocation, setSelectedLocation] = React.useState('0');
  const [selectedDeliveryStatus, setSelectedDeliveryStatus] = React.useState('all');
  const [state, setState] = React.useState({
    loading: true,
    initialized: false,
    error: '',
    rows: [],
    stale: false,
    lastUpdated: null
  });
  const [nowMs, setNowMs] = React.useState(Date.now());
  const [courierActions, setCourierActions] = React.useState({});
  const [courierBusy, setCourierBusy] = React.useState(false);
  const requestSequence = React.useRef(0);
  const reportQueryRef = React.useRef({ client: null, key: '' });
  const lastReportRequestAt = React.useRef(0);
  const lastBusinessDate = React.useRef(today);
  const courierRequestRef = React.useRef(null);
  const courierTokenRef = React.useRef(0);

  React.useEffect(() => {
    const timer = setInterval(() => setNowMs(Date.now()), 1000);
    return () => clearInterval(timer);
  }, []);

  React.useEffect(() => {
    setCourierActions({});
    setCourierBusy(false);
    return () => {
      courierTokenRef.current += 1;
      courierRequestRef.current?.abort?.();
      courierRequestRef.current = null;
    };
  }, [client, selectedDate, selectedLocation, selectedDeliveryStatus]);

  const loadOrders = React.useCallback(async (date, location, deliveryStatus) => {
    const requestId = requestSequence.current + 1;
    requestSequence.current = requestId;
    const query = { date };
    if (location !== '0') query.location = location;
    if (deliveryStatus !== 'all') query.deliveryStatus = deliveryStatus;
    const key = JSON.stringify(query);
    const sameClient = reportQueryRef.current.client === client;
    const sameQuery = sameClient && reportQueryRef.current.key === key;
    reportQueryRef.current = { client, key };
    lastReportRequestAt.current = Date.now();
    setNowMs(Date.now());
    setState(current => ({
      ...current,
      loading: true,
      initialized: sameClient && current.initialized,
      error: '',
      rows: sameClient ? current.rows : [],
      stale: false,
      lastUpdated: sameQuery ? current.lastUpdated : null
    }));
    try {
      const response = await BackofficeReportsApi.getDeliveryOrdersAsync(client, query);
      if (!response || !Array.isArray(response.results)) {
        throw new Error('Invalid delivery orders response.');
      }
      if (requestSequence.current === requestId) {
        setState({
          loading: false, initialized: true, error: '', rows: response.results,
          stale: false, lastUpdated: Date.now()
        });
      }
    } catch (error) {
      if (requestSequence.current === requestId) {
        setState(current => {
          const stale = sameQuery && current.lastUpdated !== null
            && ![400, 401, 403, 404].includes(error?.status)
            && error?.message !== 'Invalid delivery orders response.';
          return {
            loading: false, initialized: true, error: getErrorMessage(error),
            rows: stale ? current.rows : [], stale,
            lastUpdated: stale ? current.lastUpdated : null
          };
        });
      }
    }
  }, [client]);

  React.useEffect(() => {
    loadOrders(selectedDate, selectedLocation, selectedDeliveryStatus);
    return () => {
      requestSequence.current += 1;
    };
  }, [loadOrders, selectedDate, selectedDeliveryStatus, selectedLocation]);

  React.useEffect(() => {
    if (!state.initialized || state.loading) return undefined;
    const refreshIfDue = () => {
      setNowMs(Date.now());
      if (document.visibilityState !== 'visible' || Date.now() - lastReportRequestAt.current < AUTO_REFRESH_MS) return;
      const currentBusinessDate = getBusinessDate();
      const previousBusinessDate = lastBusinessDate.current;
      lastBusinessDate.current = currentBusinessDate;
      if (selectedDate === previousBusinessDate && currentBusinessDate !== previousBusinessDate) {
        setSelectedDate(currentBusinessDate);
      } else {
        loadOrders(selectedDate, selectedLocation, selectedDeliveryStatus);
      }
    };
    const timer = setTimeout(refreshIfDue, Math.max(0, AUTO_REFRESH_MS - (Date.now() - lastReportRequestAt.current)));
    document.addEventListener('visibilitychange', refreshIfDue);
    return () => {
      clearTimeout(timer);
      document.removeEventListener('visibilitychange', refreshIfDue);
    };
  }, [loadOrders, selectedDate, selectedDeliveryStatus, selectedLocation, state.initialized, state.loading]);

  const refreshCourier = order => {
    if (courierRequestRef.current || state.loading || state.stale || order.courierRefresh?.eligible !== true) return;
    const orderId = order.iref;
    const generation = order.courierDispatch?.generation;
    const token = ++courierTokenRef.current;
    const request = BackofficeReportsApi.refreshDeliveryCourierAsync(client, orderId, selectedDate);
    courierRequestRef.current = request;
    setCourierBusy(true);
    setCourierActions(current => ({ ...current, [orderId]: { generation, loading: true, error: '' } }));
    Promise.resolve(request).then(data => {
      if (!validDateTime(data?.nextAllowedOn)) throw new Error('Invalid courier refresh response.');
      if (courierTokenRef.current !== token) return;
      setNowMs(Date.now());
      setCourierActions(current => ({ ...current, [orderId]: {
        generation, loading: false, error: '', nextAllowedOn: data.nextAllowedOn
      } }));
      loadOrders(selectedDate, selectedLocation, selectedDeliveryStatus);
    }).catch(error => {
      if (courierTokenRef.current !== token) return;
      const nextAllowedOn = validDateTime(error?.responseJSON?.nextAllowedOn)
        ? error.responseJSON.nextAllowedOn : null;
      setNowMs(Date.now());
      setCourierActions(current => ({ ...current, [orderId]: {
        generation,
        loading: false,
        error: error?.status === 429
          ? 'Courier refresh is limited. Use the timer before trying again.'
          : 'Courier details could not be refreshed. The stored report will be checked again.',
        nextAllowedOn
      } }));
      loadOrders(selectedDate, selectedLocation, selectedDeliveryStatus);
    }).finally(() => {
      if (courierTokenRef.current === token) {
        courierRequestRef.current = null;
        setCourierBusy(false);
      }
    });
  };

  const moveDate = days => setSelectedDate(date => shiftIsoDate(date, days));

  return (
    <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
      <Box
        sx={{
          px: { xs: 0.75, sm: 1, md: 1.25 },
          py: { xs: 0.65, sm: 0.75 },
          flexShrink: 0,
          borderBottom: '1px solid rgba(15, 23, 42, 0.06)',
          background: 'linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)'
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: { xs: 'stretch', sm: 'center' },
            justifyContent: 'space-between',
            flexDirection: { xs: 'column', sm: 'row' },
            gap: 0.75
          }}
        >
          <Box sx={{ minWidth: 0, flexShrink: 0 }}>
            <Typography
              sx={{
                color: '#1a237e',
                fontWeight: 800,
                fontSize: '1rem',
                whiteSpace: 'nowrap'
              }}
            >
              Delivery orders
            </Typography>
            <Typography
              sx={{
                color: '#64748b',
                fontWeight: 600,
                fontSize: '0.76rem',
                whiteSpace: 'nowrap'
              }}
            >
              {state.loading
                ? state.initialized ? 'Updating stored delivery records…' : 'Loading stored delivery records…'
                : `${state.rows.length} ${state.rows.length === 1 ? 'order' : 'orders'}`} · Promised date
            </Typography>
            {state.initialized && !state.loading && (
              <Typography variant="caption" sx={{ display: 'block', color: '#64748b', fontVariantNumeric: 'tabular-nums' }}>
                {document.visibilityState === 'visible'
                  ? `Next stored-data refresh in ${formatRemaining(Math.min(AUTO_REFRESH_MS, AUTO_REFRESH_MS - (nowMs - lastReportRequestAt.current)))}`
                  : 'Automatic refresh paused while tab is hidden'}
              </Typography>
            )}
          </Box>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: { xs: 'flex-start', sm: 'flex-end' },
              flexWrap: 'wrap',
              columnGap: 0.5,
              rowGap: 1.5,
              width: { xs: '100%', sm: 'auto' }
            }}
          >
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                gap: 0.5,
                width: { xs: '100%', sm: 310 }
              }}
            >
              <TextField
                select
                label="Location"
                size="small"
                value={selectedLocation}
                onChange={event => setSelectedLocation(String(event.target.value))}
                sx={{ minWidth: 0, '& .MuiInputBase-root': { height: 36, borderRadius: '12px' } }}
              >
                <MenuItem value="0">All locations</MenuItem>
                {locations.map(location => (
                  <MenuItem key={location.iref} value={String(location.iref)}>
                    {location.name}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                label="Delivery status"
                size="small"
                value={selectedDeliveryStatus}
                onChange={event => setSelectedDeliveryStatus(String(event.target.value))}
                sx={{ minWidth: 0, '& .MuiInputBase-root': { height: 36, borderRadius: '12px' } }}
              >
                <MenuItem value="all">All statuses</MenuItem>
                {DELIVERY_STATUS_OPTIONS.map(option => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            </Box>
            <Stack
              data-testid="delivery-date-navigation"
              direction="row"
              spacing={0.25}
              alignItems="center"
              sx={{
                flexWrap: 'nowrap',
                width: { xs: '100%', sm: 'auto' },
                minWidth: 0,
                '& > *': { flexShrink: 0 }
              }}
            >
              <Tooltip title="Previous day">
                <IconButton size="small" aria-label="Previous day" onClick={() => moveDate(-1)}>
                  <NavigateBeforeIcon />
                </IconButton>
              </Tooltip>
              <TextField
                label="Promised date"
                type="date"
                size="small"
                value={selectedDate}
                onChange={event => {
                  if (event.target.value) setSelectedDate(event.target.value);
                }}
                InputLabelProps={{ shrink: true }}
                inputProps={{ min: '2000-01-01', max: today }}
                sx={{
                  width: { xs: 160, sm: 165 },
                  flexShrink: 0,
                  '& .MuiInputBase-root': { height: 36, borderRadius: '12px' }
                }}
              />
              <Tooltip title="Next day">
                <span>
                  <IconButton
                    size="small"
                    aria-label="Next day"
                    disabled={selectedDate >= today}
                    onClick={() => moveDate(1)}
                  >
                    <NavigateNextIcon />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Go to today">
                <span>
                  <IconButton
                    size="small"
                    aria-label="Go to today"
                    disabled={selectedDate === today}
                    onClick={() => setSelectedDate(today)}
                  >
                    <CalendarTodayIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Refresh stored data (automatic every minute while visible)">
                <IconButton
                  size="small"
                  aria-label="Refresh delivery orders"
                  onClick={() => loadOrders(
                    selectedDate,
                    selectedLocation,
                    selectedDeliveryStatus
                  )}
                >
                  <RefreshIcon />
                </IconButton>
              </Tooltip>
            </Stack>
          </Box>
        </Box>
      </Box>

      {state.loading && state.initialized && <LinearProgress aria-label="Loading delivery orders" />}

      {state.stale && (
        <Alert severity="warning" sx={{ mx: 1, mt: 1 }} action={(
          <Button color="inherit" size="small" onClick={() => loadOrders(
            selectedDate, selectedLocation, selectedDeliveryStatus
          )}>Retry</Button>
        )}>
          {state.error} Showing previously loaded data from {formatDateTime(state.lastUpdated)} (Bucharest).
        </Alert>
      )}

      <Box
        aria-busy={state.loading}
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          opacity: state.loading && state.initialized ? 0.55 : 1,
          pointerEvents: state.loading && state.initialized ? 'none' : 'auto',
          transition: 'opacity 120ms ease'
        }}
      >
        {state.loading && !state.initialized ? (
          <Stack alignItems="center" justifyContent="center" sx={{ minHeight: 180 }}>
            <CircularProgress size={28} />
          </Stack>
        ) : state.error && !state.stale ? (
          <Alert
            severity="error"
            action={(
              <Button
                color="inherit"
                size="small"
                onClick={() => loadOrders(
                  selectedDate,
                  selectedLocation,
                  selectedDeliveryStatus
                )}
              >
                Retry
              </Button>
            )}
            sx={{ m: 1 }}
          >
            {state.error}
          </Alert>
        ) : state.rows.length === 0 ? (
          <Stack alignItems="center" justifyContent="center" sx={{ minHeight: 180, px: 2 }}>
            <Typography color="text.secondary">
              No delivery orders match the selected date and filters.
            </Typography>
          </Stack>
        ) : (
          state.rows.map(order => (
            <DeliveryOrderRow
              key={order.iref}
              order={order}
              onRefreshCourier={refreshCourier}
              action={courierActions[order.iref]}
              nowMs={nowMs}
              reportBusy={state.loading || state.stale}
              courierBusy={courierBusy}
            />
          ))
        )}
      </Box>
    </Box>
  );
};

export default DeliveryOrders;
